/**
 * Contactos desglosados (puro). La API manda en cada bloque `metrics` (anuncio,
 * campaña, `account_totals`, `totals`, filas por moneda) `wa_conversations`
 * (conversaciones de WhatsApp iniciadas), `leads` (formularios de Meta) y
 * `contacts` = las dos sumadas (`MetaAds/metrics.py`).
 *
 * - «72 conversaciones + 3 formularios»; si uno es 0 o falta, solo el otro.
 * - Sin `wa_conversations` ni `leads` (API vieja / fixture): «N contactos» como antes.
 * - Los dos en 0: «0 contactos» (lo de siempre).
 * - El total queda en `note` («75 contactos en total») para un Tooltip.
 */
const num = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const contactsText = (n) => plural(n, 'contacto', 'contactos');

/**
 * @param {object|null} metrics Bloque `metrics` (o fila inline con los mismos campos).
 * @returns {{ text: string, total: number, breakdown: boolean, note: string|null }|null}
 *   `null` si no hay ningún dato de contactos.
 */
export function metaContactsInfo(metrics) {
  if (!metrics || typeof metrics !== 'object') return null;
  const contacts = num(metrics.contacts);
  const wa = num(metrics.wa_conversations);
  const leads = num(metrics.leads);
  if (wa == null && leads == null) {
    return contacts == null ? null : { text: contactsText(contacts), total: contacts, breakdown: false, note: null };
  }
  const total = contacts ?? ((wa || 0) + (leads || 0));
  const parts = [];
  if (wa) parts.push(plural(wa, 'conversación', 'conversaciones'));
  if (leads) parts.push(plural(leads, 'formulario', 'formularios'));
  if (!parts.length) return { text: contactsText(total), total, breakdown: false, note: null };
  return { text: parts.join(' + '), total, breakdown: true, note: `${contactsText(total)} en total` };
}

/**
 * Versión corta (ficha, para que la línea entre en una): siempre el total («75 contactos»); el
 * desglose pasa a `metaContactsShortNote` (ⓘ / Tooltip). Sin datos → ''.
 */
export function formatMetaContactsShort(metrics) {
  const info = metaContactsInfo(metrics);
  return info ? contactsText(info.total) : '';
}

/** Nota de la versión corta: «75 contactos = 72 conversaciones + 3 formularios» o `null` sin desglose. */
export function metaContactsShortNote(metrics) {
  const info = metaContactsInfo(metrics);
  return info?.breakdown ? `${contactsText(info.total)} = ${info.text}` : null;
}

/** Solo el texto («72 conversaciones + 3 formularios» / «N contactos»); sin datos → ''. */
export function formatMetaContacts(metrics) {
  return metaContactsInfo(metrics)?.text || '';
}

/** Nota para el Tooltip («75 contactos en total») o `null` si no hay desglose. */
export function metaContactsNote(metrics) {
  return metaContactsInfo(metrics)?.note || null;
}
